/**
 * Offline Mode Manager
 * Handles logged-out and offline functionality using CRDT without server sync.
 * Extracted from main.js for better separation of concerns.
 *
 * Used for: not-logged-in users and session-expired recovery.
 */

import { emit, Events } from "../../../engine/core/events.js";
import * as LoadProgress from "../ui/load-progress.js";
import { t } from "../../../shared/i18n/index.js";
import { prepareDeviceMapKey } from "../../../engine/services/attachment-service.js";
import { SCHEMA_V2 } from "../../../engine/sync/map-format.js";
import { migrateOpenMapToV2 } from "../../../engine/sync/map-format-migration.js";
import { dropOpenMapOldCopy } from "../../../engine/sync/old-copy-drop.js";
import { isLocalSlotEnabled } from "../../../engine/crypto/vault-storage.js";
import { engine } from "../../../engine/index.js";

// Stored callbacks
let cb = null;

/**
 * Setup offline mode manager with callbacks.
 *
 * @param {Object} callbacks - Dependencies:
 *   - initializeAppCore: () => Promise<void>
 *   - initVaultStorage: (config) => void
 *   - enableUserCRDT: (userId, masterKey, options) => Promise<{hasData}>
 *   - VaultStorage: VaultStorage module
 *   - VaultUI: VaultUI module
 *   - CRDTBridge: CRDTBridge module
 *   - Store: Store module
 *   - getOrCreateLocalUser: () => {user_id}
 *   - getDocState: (docId) => object|null
 *   - restoreSidebarState: (docState) => void
 *   - getLastOpenedDoc: () => {docId}|null
 *   - setLastOpenedDoc: (docId, synced) => void
 *   - clearLastOpenedDoc: () => void
 *   - stopCRDTSync: () => Promise<void>
 *   - generateId: () => string
 *   - ROOT_COLOR: string
 *   - markSaved: () => void
 *   - renderTree: () => void
 *   - switchView: (viewName) => void
 */
export function setupOfflineMode(callbacks) {
  cb = callbacks;
}

/**
 * Seed default tag definitions / kanban columns on a freshly-created
 * doc in logged-out / Tauri local-only mode. In synced mode the
 * sync-orchestrator does this in its post-sync callback; logged-out
 * docs never reach SYNCED, so without this call new local docs are
 * missing the mandatory tag triplet (Todo / In Progress / Done) and
 * default kanban columns.
 *
 * **Only call this on docs we know are brand new** — never on
 * pre-existing docs being re-opened. The underlying initDefaultTags()
 * re-adds any mandatory tag that's currently missing, which would
 * silently override a user who deliberately deleted "Done" or
 * renamed the triplet. We can't tell from doc state alone whether a
 * gap is "never seeded" or "intentionally removed", so we default to
 * non-action and only seed on creation.
 *
 * Wrapped in try/catch because StorageHealth.assertHealthy() can
 * throw on a pre-broken storage state — we don't want that to fail
 * the doc-create flow (the defaults are nice-to-have, not
 * load-critical).
 */
function runPerDocMigrations() {
  try {
    cb.CRDTBridge.migrateDocNotes();
  } catch (err) {
    console.warn("[LoggedOut] per-doc migrations skipped:", err?.message || err);
  }
}

/**
 * Initialize app in logged-out mode using CRDT without server sync.
 * Used for: not-logged-in users and session-expired recovery.
 *
 * @param {Object} options
 * @param {string} options.userId - User ID (local or cached real user)
 * @param {Uint8Array} options.masterKey - Encryption key for CRDT data
 * @param {boolean} options.isLoggedOut - True if this is logged-out mode (not session recovery)
 */
export async function initializeLoggedOutMode({ userId, masterKey, isLoggedOut = true }) {
  console.log(`[App] Initializing ${isLoggedOut ? 'logged-out' : 'offline'} mode for user:`, userId);

  // Initialize core UI (views, modals, keyboard, etc.)
  await cb.initializeAppCore();

  // Initialize VaultStorage with logged-out/offline config
  // This sets up encryption but we won't enable sync
  cb.initVaultStorage({
    getMasterKey: () => masterKey,
    getAuthHeaders: () => ({}), // No auth in logged-out mode
    getUserId: () => userId,
    onSaveComplete: cb.markSaved,
    getTemplateData: () => ({
      id: cb.generateId(),
      name: t("app.starter.project"),
      color: cb.ROOT_COLOR,
      children: [{
        id: cb.generateId(),
        name: t("app.starter.getStarted"),
        color: cb.ROOT_COLOR,
        children: [],
        notes: ""
      }],
      notes: ""
    })
  });

  // Enable User CRDT for vault index WITHOUT sync
  // This loads from IndexedDB but doesn't connect to server
  await cb.enableUserCRDT(userId, masterKey, {
    isNewUser: false,  // Never treat logged-out as "new user" (no server confirmation needed)
    skipSync: true     // Don't connect WebSocket
  });

  // Get vault index
  let index = cb.VaultStorage.getIndex();

  // If the vault has no map (online or on this device), create a default
  // map. Track this so we only seed mandatory tags on a doc we know we just
  // created — never on an existing doc, where missing tags might reflect
  // deliberate user curation rather than a bug. Without a session the
  // server cannot register an online map (docs/architecture/sync.md, "Vault
  // repair"): it is made on this device.
  let createdStarterDoc = false;
  if (!index || (index.docs || []).length === 0) {
    console.log("[App] No existing local data, creating default map");
    await cb.VaultStorage.createMap(t("app.starter.firstMap"), null, null, isLocalSlotEnabled() ? { storage: "local" } : null);
    index = cb.VaultStorage.getIndex();
    createdStarterDoc = true;
  }

  // Initialize VaultUI with VaultStorage for logged-out mode
  cb.VaultUI.init({
    storage: cb.VaultStorage,
    onDocOpen: handleLoggedOutDocOpen,
    onDocCreate: handleLoggedOutDocCreate,
    onDocDelete: handleLoggedOutDocDelete,
  });

  // Reopen the map this tab (or, failing that, this browser) last had open,
  // like the signed-in path does; otherwise the first map.
  const lastOpenedId = cb.getLastOpenedDoc?.()?.docId;
  const reopenId = lastOpenedId && index.docs?.some((d) => d.docId === lastOpenedId) ? lastOpenedId : null;
  const currentDocId = cb.VaultStorage.getCurrentDocId() || reopenId || index.docs?.[0]?.docId;

  if (currentDocId) {
    // Set current doc ID for VaultUI
    cb.VaultStorage.setCurrentDocId(currentDocId);

    // Initialize CRDT for this document WITHOUT sync
    await cb.CRDTBridge.init(currentDocId, { userId });
    // Existing local docs need the same additive execution reconciliation as
    // online opens, even when their tags/columns must be left untouched.
    cb.CRDTBridge.migrateRefnotesToExecution();
    // Only run the full per-doc migrations (kanban columns included) on the
    // freshly-created starter doc: a user may have curated an existing doc's
    // columns. The Todo / In Progress / Done tags can't be deleted or renamed,
    // so any existing doc missing them gets them.
    if (createdStarterDoc) {
      runPerDocMigrations();
    } else {
      cb.CRDTBridge.ensureDefaultTags();
    }
    // Maps here are on this device only: no sync to wait for.
    await migrateOpenMapToV2(cb.CRDTBridge);
    // Two weeks after the conversion, its old copy (plan 2026-10-13-drop-schema-v1.md).
    await dropOpenMapOldCopy(cb.CRDTBridge, { online: false })
      .catch((err) => console.warn("[OfflineMode] The map keeps its old copy for now:", err));
    // Load persisted state from IndexedDB (no sync)
    const treeData = cb.CRDTBridge.getTreeFromCRDT();
    const rootId = treeData?.id;

    // Maps signed out are on this device: their attachments' key first.
    await prepareDeviceMapKey(currentDocId);

    if (treeData) {
      // Restore UI state from localStorage
      const localState = cb.getDocState(currentDocId) || {};
      cb.Store.initialize(treeData, {
        focusId: localState.focusId || rootId,
        selectedId: localState.selectedId || null,
        view: localState.view || 'dashboard',
        docId: currentDocId,
        ...localState
      });

      cb.restoreSidebarState?.(localState);

      cb.renderTree();
      cb.switchView(localState.view || 'dashboard');
      cb.VaultUI.updateCurrentDocName();
    } else {
      // CRDT exists but is empty (shouldn't happen normally)
      console.warn("[App] CRDT doc exists but has no tree data");
      // The top node carries the map's title, as for maps made in the vault.
      const title = index.docs?.find((d) => d.docId === currentDocId)?.title;
      const templateData = {
        id: cb.generateId(),
        name: title || t("app.starter.project"),
        color: cb.ROOT_COLOR,
        children: [{
          id: cb.generateId(),
          name: t("app.starter.getStarted"),
          color: cb.ROOT_COLOR,
          children: [],
          notes: ""
        }],
        notes: ""
      };
      cb.Store.initialize(templateData, { view: 'dashboard', docId: currentDocId });
      // The starter map just created starts in schema v2; any other empty
      // map stays v1 (its content may be elsewhere) and moves when opened.
      cb.CRDTBridge.syncFullTree(cb.Store.get('data'), createdStarterDoc ? { schema: SCHEMA_V2 } : {});
      cb.renderTree();
      cb.switchView('dashboard');
      cb.VaultUI.updateCurrentDocName();
    }
  }

  console.log("[App] Logged-out mode initialization complete");
  emit(Events.APP_STARTED, {});
}

/**
 * Logged-out mode doc open handler - loads from CRDT without server sync.
 */
export async function handleLoggedOutDocOpen(docId) {
  console.log("[LoggedOut] Opening doc:", docId);

  LoadProgress.beginBar(t("app.load.openingMap"));
  LoadProgress.stage(0.2, t("app.load.readingLocal"));

  try {
    return await _doLoggedOutDocOpen(docId);
  } finally {
    LoadProgress.doneBar();
  }
}

async function _doLoggedOutDocOpen(docId) {
  // The engine opens it from this device (engine/open-map.js; no
  // per-map board columns seeded on an existing map).
  let localState = {};
  const opened = await engine.maps.open(docId, {
    remember: false,   // kept below
    onProgress: (fraction, stage) => {
      if (stage === "building") LoadProgress.stage(fraction, t("app.load.preparingView"));
    },
    onOpened: (tree) => {
      localState = cb.getDocState(docId) || {};
      cb.Store.initialize(tree, {
        focusId: localState.focusId || tree.id,
        selectedId: localState.selectedId || null,
        view: localState.view || 'dashboard',
        docId: docId,
        ...localState
      });
    },
  });
  if (!opened) return;

  cb.restoreSidebarState?.(localState);
  cb.renderTree();
  cb.switchView(localState.view || 'dashboard');
  // (A map open by its link for this page is not reopened at the next start.)
  if (!engine.sharing.link.isLinkMap(docId)) cb.setLastOpenedDoc(docId, false);
  cb.VaultUI.updateCurrentDocName();
}

/**
 * Logged-out mode doc create handler: the engine gives the new map its
 * first content as it opens it (engine/open-map.js).
 */
export async function handleLoggedOutDocCreate(docId) {
  console.log("[LoggedOut] Doc created:", docId);
  return handleLoggedOutDocOpen(docId);
}

/**
 * Logged-out mode doc delete handler.
 */
export async function handleLoggedOutDocDelete(docId) {
  console.log("[LoggedOut] Doc deleted:", docId);
  const currentDocId = cb.VaultStorage.getCurrentDocId();
  if (docId === currentDocId) {
    const index = cb.VaultStorage.getIndex();
    if (index && (index.docs || []).length > 0) {
      await handleLoggedOutDocOpen(index.docs[0].docId);
    } else {
      try {
        await cb.stopCRDTSync?.();
      } catch (err) {
        console.warn("[LoggedOut] Failed to stop CRDT after deleting active doc:", err);
      }
      cb.VaultStorage.setCurrentDocId(null);
      cb.Store.reset?.();
      cb.clearLastOpenedDoc?.();
      cb.markSaved?.();
      // Mirror main.js clearActiveDocumentAfterDelete: notify subscribers
      // (mobile shell, vault UI) so they refresh after the doc list shrinks
      // to zero. Without this, mobile keeps showing stale doc state until
      // the next unrelated event triggers a render.
      try {
        emit(Events.VAULT_INDEX_UPDATED, { source: "logged-out-doc-delete" });
      } catch (err) {
        // Best-effort: Store.reset() already covered the main state change.
      }
    }
  }
}
